import { IAutoMapper } from "../../../../../../core/automapper/interfaces";
import { StringHelper } from "../../../../../../utilities/contracts/string-helper";
import { DEFAULT_REVIEW_DEADLINE_DAYS, ReviewDeadlineState, businessDaysLeft, getReviewDeadlineState } from "./review-deadline";

const OVERREADER2_ROLE_NAME = "Overreader2";

export const mapFeedbackChallengeQueryItem = (automapper: IAutoMapper): void => {
  automapper.createMap("default", "FeedbackChallengeQueryItem")
    .forMember("chaseId", o => o.chaseId)
    .forMember("projectId", o => o.projectId)
    .forMember("projectName", o => o.projectName)
    .forMember("measureCode", o => o.measureCode)
    .forMember("codingUserId", o => o.codingUserId)
    .forMember("codingUserName", o => o.codingUserName)
    .forMember("codingDate", o => o.codingDate)
    .forMember("qaUserId", o => o.qaUserId)
    .forMember("qaUserName", o => o.qaUserName)
    .forMember("qaRoleName", o => o.qaRoleName)
    .forMember("qaDate", o => o.qaDate)
    .forMember("overreadLevel", o => o.qaRoleName === OVERREADER2_ROLE_NAME ? "OR II" : "OR I")
    .forMember("managerUserId", o => o.managerUserId)
    .forMember("managerUserName", o => o.managerUserName)
    .forMember("challengeDate", o => o.challengeDate)
    .forMember("reviewDeadlineDays", o => o.reviewDeadlineDays)
    .forMember("errorCount", o => o.errorCount)
    .forMember("errorReviewCompletedCount", o => o.errorReviewCompletedCount)
    .forMember("errorReviewToBeCompletedCount", o => o.errorReviewToBeCompletedCount)
    .forMember("chaseOverreadFeedbackStatus", o => o.chaseOverreadFeedbackStatus)
    .forMember("codingReviewMode", o => o.codingReviewMode)
    .forMember("recordCount", o => o.recordCount);
};

export class FeedbackChallengeQueryItem {
  chaseId: number;
  projectId: number;
  projectName: string;
  measureCode: string;
  codingUserId: number;
  codingUserName: string;
  codingDate: Date;
  qaUserId: number;
  qaUserName: string;
  qaRoleName: string;
  qaDate: Date;
  overreadLevel: string;
  managerUserId: number;
  managerUserName: string;
  /** Creation date of the open challenge with the fewest days left on this chase and overread level. */
  challengeDate: Date;
  /** Set Manager Rebuttal Review Deadline for this overread level, in business days. */
  reviewDeadlineDays: number;
  errorCount: number;
  errorReviewCompletedCount: number;
  errorReviewToBeCompletedCount: number;
  chaseOverreadFeedbackStatus: string;
  codingReviewMode: string;
  recordCount: number;

  constructor(options: Partial<FeedbackChallengeQueryItem>) {
    this.chaseId = options.chaseId;
    this.projectId = options.projectId;
    this.projectName = StringHelper.clean(options.projectName);
    this.measureCode = StringHelper.clean(options.measureCode);
    this.codingUserId = options.codingUserId;
    this.codingUserName = StringHelper.clean(options.codingUserName);
    this.codingDate = options.codingDate;
    this.qaUserId = options.qaUserId;
    this.qaUserName = StringHelper.clean(options.qaUserName);
    this.qaRoleName = StringHelper.clean(options.qaRoleName);
    this.qaDate = options.qaDate;
    this.overreadLevel = StringHelper.clean(options.overreadLevel);
    this.managerUserId = options.managerUserId;
    this.managerUserName = StringHelper.clean(options.managerUserName);
    this.challengeDate = options.challengeDate;
    this.reviewDeadlineDays = options.reviewDeadlineDays;
    this.errorCount = options.errorCount;
    this.errorReviewCompletedCount = options.errorReviewCompletedCount;
    this.errorReviewToBeCompletedCount = options.errorReviewToBeCompletedCount;
    this.chaseOverreadFeedbackStatus = StringHelper.clean(options.chaseOverreadFeedbackStatus);
    this.codingReviewMode = options.codingReviewMode;
    this.recordCount = options.recordCount;
  }

  /** Chase-level review progress shown on the Complete tab, e.g. "3 of 5 reviewed". */
  get reviewProgressText(): string {
    const completed = this.errorReviewCompletedCount ?? 0;
    const total = completed + (this.errorReviewToBeCompletedCount ?? 0);
    return total === 0 ? "" : `${completed} of ${total} reviewed`;
  }

  get reviewDeadline(): number {
    return this.reviewDeadlineDays > 0 ? this.reviewDeadlineDays : DEFAULT_REVIEW_DEADLINE_DAYS;
  }

  /** Business days left to decide; null when the challenge date isn't known. Recomputed on every read, so it follows the calendar. */
  get daysLeft(): number | null {
    return this.challengeDate == null ? null : businessDaysLeft(new Date(this.challengeDate), this.reviewDeadline, new Date());
  }

  get daysLeftText(): string {
    return this.daysLeft == null ? "" : `${this.daysLeft} of ${this.reviewDeadline} days left`;
  }

  get reviewDeadlineState(): ReviewDeadlineState | null {
    return this.daysLeft == null ? null : getReviewDeadlineState(this.daysLeft);
  }

  /** Share of the deadline already used, 0-100, for the progress bar. */
  get reviewDeadlineProgress(): number {
    return this.daysLeft == null ? 0 : Math.round((this.reviewDeadline - this.daysLeft) / this.reviewDeadline * 100);
  }
}
